import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDeleteDelegationMutation, useGetDelegationsQuery, useBulkDeleteDelegationsMutation, useBulkRevokeDelegationsMutation, useRevokeDelegationMutation, useBulkManualAssignMutation, useCreateManualDelegationMutation } from "../../utils/api/endpoints/delegationsApi";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import UserSearchDropdown, { UserOption } from "../../components/delegations/UserSearchDropdown";
import { FiChevronDown } from "react-icons/fi";
import RetestIcon from "../../assets/icons/retest-dele-green.svg";
import ReportDelegateIcon from "../../assets/icons/report-delegate.svg";
import ReportDelegateBlueIcon from "../../assets/icons/report-del-blue.svg";

const statuses = ["pending", "accepted", "rejected", "expired", "revoked"] as const;
const scopes = ["retest", "report_comment"] as const;

type Scope = typeof scopes[number];

type FilterState = {
  scope: Scope | "";
  status: string;
  to_user_id?: number;
  to_user?: UserOption;
  from_user_id?: number;
  from_user?: UserOption;
  program_id?: number;
  object_id?: number;
  report_title?: string;
  page: number;
  limit: number;
};

const DelegationManagement = () => {
  const { role, id: currentUserId } = useUserCredentials();
  const allowed = role === UserRole.ADMIN || role === UserRole.SUB_ADMIN || role === UserRole.QA || role === UserRole.ADMIN_MANAGER;

  const [filters, setFilters] = useState<FilterState>({ scope: "", status: "", page: 1, limit: 20 });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { data, isFetching, refetch } = useGetDelegationsQuery({
    ...filters,
    scope: filters.scope || undefined,
    status: filters.status || undefined,
    report_title: filters.report_title || undefined
  } as any);

  const [selected, setSelected] = useState<string[]>([]);
  const [revokeDelegation] = useRevokeDelegationMutation();
  const [deleteDelegation] = useDeleteDelegationMutation();
  const [bulkRevoke] = useBulkRevokeDelegationsMutation();
  const [bulkDelete] = useBulkDeleteDelegationsMutation();
  const [createManual] = useCreateManualDelegationMutation();
  const [bulkManual] = useBulkManualAssignMutation();

  const rows = data?.data || [];
  const count = data?.count || 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / (filters.limit || 20))), [count, filters.limit]);
  const [showAssignments, setShowAssignments] = useState(false);
  const assignmentsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (showAssignments && assignmentsRef.current) {
      assignmentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showAssignments]);

  const statusBadge = (status: string, isDeleted?: boolean, deletedAt?: string) => {
    const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border shadow-sm";
    const iconMap: Record<string, JSX.Element> = {
      pending: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>,
      accepted: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>,
      rejected: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>,
      expired: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>,
      revoked: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>,
      deleted: <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
    };
    const map: Record<string, string> = {
      pending: `${base} bg-ctb-orange-100 text-ctb-orange-200 border-ctb-yellow-250`,
      accepted: `${base} bg-[#D1FAE5] text-ctb-green-300 border-ctb-green-300`,
      rejected: `${base} bg-[#FECACA] text-[#B91C1C] border-red-300`,
      expired: `${base} bg-ctb-grey-50 text-ctb-grey-600 border-ctb-grey-200`,
      revoked: `${base} bg-ctb-grey-150 text-ctb-grey-800 border-ctb-grey-450`,
      deleted: `${base} bg-red-100 text-red-700 border-red-300`
    };

    // If deleted, show "Deleted" status regardless of original status
    const displayStatus = isDeleted ? "deleted" : status;
    const displayText = isDeleted
      ? `Deleted (was ${status.charAt(0).toUpperCase() + status.slice(1)})`
      : status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <span className={map[displayStatus] || base} title={deletedAt ? `Deleted on ${new Date(deletedAt).toLocaleString()}` : undefined}>
        {iconMap[displayStatus]}
        {displayText}
      </span>
    );
  };

  const scopeBadge = (scope: Scope) => {
    const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border shadow-sm";
    const isRetest = scope === "retest";
    const cls = isRetest
      ? `${base} bg-ctb-blue-150 text-ctb-blue-200 border-ctb-blue-250`
      : `${base} bg-ctb-blue-0 text-ctb-blue-400 border-ctb-blue-100`;
    const icon = isRetest ? (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
    ) : (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" /></svg>
    );
    return (
      <span className={cls}>
        {icon}
        {isRetest ? "Retest" : "Report Comment"}
      </span>
    );
  };

  const [manualForm, setManualForm] = useState<{ scope: Scope; object_id?: number; to_user_id?: number; to_user?: UserOption; reason?: string }>({ scope: "retest" });
  const [bulkAssignmentsText, setBulkAssignmentsText] = useState<string>("");

  const onToggleSelect = (id: string, checked: boolean) => {
    setSelected(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };

  const onSelectAll = (checked: boolean) => {
    setSelected(checked ? rows.map((r: any) => r.id) : []);
  };

  const handleRevoke = async (id: string) => {
    await revokeDelegation({ id }).unwrap();
    refetch();
  };

  const handleDelete = async (id: string) => {
    await deleteDelegation({ id }).unwrap();
    refetch();
  };

  const handleBulkRevoke = async () => {
    if (!selected.length) return;
    await bulkRevoke({ ids: selected }).unwrap();
    setSelected([]);
    refetch();
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    await bulkDelete({ ids: selected }).unwrap();
    setSelected([]);
    refetch();
  };

  const handleManualCreate = async () => {
    if (!manualForm.to_user_id || !manualForm.object_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (currentUserId && manualForm.to_user_id === currentUserId) {
      toast.error("Cannot delegate to the same researcher");
      return;
    }
    try {
      if (manualForm.scope === "retest") {
        await createManual({ scope: "retest", retest_id: manualForm.object_id, to_user_id: manualForm.to_user_id, reason: manualForm.reason }).unwrap();
      } else {
        await createManual({ scope: "report_comment", report_id: manualForm.object_id, to_user_id: manualForm.to_user_id, reason: manualForm.reason }).unwrap();
      }
      toast.success("Delegation created successfully");
      setManualForm({ scope: manualForm.scope });
      refetch();
    } catch (error: any) {
      const reason = error?.data?.message || error?.message || "Failed to create delegation";
      toast.error(reason);
    }
  };

  const handleBulkManual = async () => {
    try {
      const assignments = JSON.parse(bulkAssignmentsText);
      if (!Array.isArray(assignments)) return;
      await bulkManual({ assignments }).unwrap();
      setBulkAssignmentsText("");
      refetch();
      toast.success("Bulk delegations submitted");
    } catch (e: any) {
      const reason = e?.data?.message || e?.message || "Failed to submit bulk delegations";
      toast.error(reason);
    }
  };

  if (!allowed) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">Access Denied</div>
          <div className="text-sm text-gray-500">You do not have access to Delegation Management.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header Card */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
                style={{ backgroundColor: "rgba(88, 204, 2, 0.17)" }}
              >
                <img src={RetestIcon} alt="Delegation icon" className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Delegation Management</h1>
                <p className="text-gray-600 text-sm mt-0.5">Manage and monitor delegation assignments</p>
              </div>
            </div>
            <div className="text-center px-6 py-3 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-xs text-gray-600 font-medium">Total Delegations</div>
            </div>
          </div>
          <div>
            <button
              onClick={() => setShowAssignments(true)}
              className="px-5 py-3 bg-[#010D2C] text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              + Create Delegation
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">

        {/* Filters Bar */}
        <div className="bg-[#010D2C] text-white rounded-2xl px-6 py-4 mb-3 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filters</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <button
              onClick={() => {
                setFilters({ scope: "", status: "", page: 1, limit: 20 });
                refetch();
              }}
              className="px-3 py-1.5 rounded-lg border border-white/30 text-white hover:bg-white/10 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilters(s => !s)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/30 text-white hover:bg-white/10 transition-colors"
            >
              <span>{showFilters ? "Hide" : "Show"}</span>
              <FiChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-2xl border border-gray-200 mb-6 shadow-sm p-6">
            {/* Primary Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ctb-blue-200"></span>
                  Module
                </label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  value={filters.scope}
                  onChange={e => setFilters(f => ({ ...f, scope: (e.target.value as Scope | "") }))}
                >
                  <option value="">All Modules</option>
                  {scopes.map(s => (
                    <option key={s} value={s}>
                      {s === "retest" ? "Retest" : "Report Comment"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ctb-green-300"></span>
                  Status
                </label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  {statuses.map(s => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ctb-blue-200"></span>
                  Report Title
                </label>
                <input
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent shadow-sm transition-all duration-200"
                  placeholder="Search by title..."
                  type="text"
                  value={filters.report_title || ""}
                  onChange={e => setFilters(f => ({ ...f, report_title: e.target.value || undefined }))}
                />
              </div>
            </div>

            {/* Secondary Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  Assigned To
                </label>
                <UserSearchDropdown
                  value={filters.to_user_id}
                  onChange={(userId, user) => setFilters(f => ({ ...f, to_user_id: userId, to_user: user }))}
                  placeholder="Search user..."
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  Assigned From
                </label>
                <UserSearchDropdown
                  value={filters.from_user_id}
                  onChange={(userId, user) => setFilters(f => ({ ...f, from_user_id: userId, from_user: user }))}
                  placeholder="Search user..."
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-500"></span>
                  Program ID
                </label>
                <input
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent shadow-sm transition-all duration-200"
                  placeholder="Enter program ID..."
                  type="number"
                  value={filters.program_id || ""}
                  onChange={e => setFilters(f => ({ ...f, program_id: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </div>
            </div>

            {/* Tertiary Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ctb-blue-200"></span>
                  Object ID
                </label>
                <input
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent shadow-sm transition-all duration-200"
                  placeholder="Enter object ID..."
                  type="number"
                  value={filters.object_id || ""}
                  onChange={e => setFilters(f => ({ ...f, object_id: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="px-6 py-2.5 bg-ctb-blue-200 text-white text-sm font-semibold rounded-lg hover:bg-ctb-blue-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                {isFetching ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Apply Filters
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selected.length > 0 && (
          <div className="bg-ctb-blue-150 border-2 border-ctb-blue-250 rounded-xl p-4 mb-6 flex items-center justify-between shadow-md animate-in slide-in-from-top duration-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-ctb-blue-200 flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <div className="text-sm font-bold text-ctb-blue">{selected.length} Delegation{selected.length > 1 ? 's' : ''} Selected</div>
                <div className="text-xs text-ctb-blue-900">Choose an action to apply</div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBulkRevoke}
                className="px-5 py-2.5 bg-ctb-yellow-250 text-white text-sm font-semibold rounded-lg hover:bg-ctb-orange-200 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Revoke Selected
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-5 py-2.5 bg-ctb-red-400 text-white text-sm font-semibold rounded-lg hover:bg-ctb-red-500 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg mb-6">
          <div className="px-6 py-4 bg-[#F8FAFC] border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-ctb-blue-150 flex items-center justify-center">
                <img src={ReportDelegateIcon} alt="Delegation records icon" className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Delegation Records</h3>
                <p className="text-xs text-gray-500">{rows.length} of {count} records displayed</p>
              </div>
            </div>
            {isFetching && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#F8FAFC]">
                <tr className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  <th className="px-5 py-4 w-12 text-left">
                    <input
                      type="checkbox"
                      checked={selected.length === rows.length && rows.length > 0}
                      onChange={e => onSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-ctb-blue-200 focus:ring-ctb-blue-200 w-4 h-4"
                    />
                  </th>
                  <th className="px-5 py-4 text-left">Module</th>
                  <th className="px-5 py-4 text-left">Status</th>
                  <th className="px-5 py-4 text-left">Report Title</th>
                  <th className="px-5 py-4 text-left">From</th>
                  <th className="px-5 py-4 text-left">To</th>
                  <th className="px-5 py-4 text-left">Created</th>
                  <th className="px-5 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white text-sm">
                {rows.map((r: any) => (
                  <tr
                    key={r.id}
                    className={`hover:bg-ctb-blue-150 transition-all duration-200 ${r.is_deleted || r.deleted_at ? 'opacity-60 bg-gray-50' : ''}`}
                  >
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(r.id)}
                        onChange={e => onToggleSelect(r.id, e.target.checked)}
                        className="rounded border-gray-300 text-ctb-blue-200 focus:ring-ctb-blue-200 w-4 h-4"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[#0F172A] text-xs font-medium shadow-sm border border-gray-200 ${
                          r.scope === "retest" ? "bg-[#D1FAE5]" : "bg-[#BFDBFE]"
                        }`}
                      >
                        <img
                          src={r.scope === "retest" ? RetestIcon : ReportDelegateBlueIcon}
                          alt={r.scope === "retest" ? "Retest" : "Report"}
                          className="w-4 h-4"
                        />
                        {r.scope === "retest" ? "Retest" : "Report Comment"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {statusBadge(r.status, r.is_deleted || !!r.deleted_at, r.deleted_at)}
                    </td>
                    <td className="px-5 py-4">
                      {r.scope === "retest" && r.Retest ? (
                        <div className="flex flex-col gap-1">
                          <a
                            className="text-[#0F172A] hover:text-ctb-blue-400 font-medium hover:underline transition-colors"
                            href={`/dashboard/retests/${r.retest_id}`}
                          >
                            {r.Retest?.report?.report_title || `Report #${r.Retest?.report?.report_id || r.report_id}`}
                          </a>
                          <span className="text-xs text-gray-500 font-medium">Retest #{r.retest_id}</span>
                        </div>
                      ) : r.Report ? (
                        <div className="flex flex-col gap-1">
                          <a
                            className="text-[#0F172A] hover:text-ctb-blue-400 font-medium hover:underline transition-colors"
                            href={`/dashboard/reports/${r.report_id}`}
                          >
                            {r.Report?.report_title || `Report #${r.report_id}`}
                          </a>
                          <span className="text-xs text-gray-500 font-medium">Report #{r.report_id}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">No data</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-gray-700 font-medium">
                      <div className="flex items-center gap-2">
                        {r.from_user_pfp || r.FromUser?.pfp ? (
                          <img
                            src={(r.from_user_pfp as string) || (r.FromUser?.pfp as string)}
                            alt={r.FromUser?.display_name || r.FromUser?.username || "from user"}
                            className="w-8 h-8 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-ctb-blue-200 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {(r.FromUser?.display_name || r.FromUser?.username || r.FromUser?.email || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <span>{r.FromUser ? (r.FromUser.display_name || r.FromUser.username || r.FromUser.email) : `User #${r.from_user_id}`}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-700 font-medium">
                      <div className="flex items-center gap-2">
                        {r.to_user_pfp || r.ToUser?.pfp ? (
                          <img
                            src={(r.to_user_pfp as string) || (r.ToUser?.pfp as string)}
                            alt={r.ToUser?.display_name || r.ToUser?.username || "to user"}
                            className="w-8 h-8 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-ctb-green-300 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {(r.ToUser?.display_name || r.ToUser?.username || r.ToUser?.email || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <span>{r.ToUser ? (r.ToUser.display_name || r.ToUser.username || r.ToUser.email) : `User #${r.to_user_id}`}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600 font-medium">
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "-"}
                    </td>
                    <td className="px-5 py-4">
                      {r.is_deleted || r.deleted_at ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-red-600 font-semibold">Deleted</span>
                          {r.deleted_at && (
                            <span className="text-xs text-gray-500">
                              {new Date(r.deleted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {r.deleted_by_user && (
                            <span className="text-xs text-gray-500">
                              by {r.deleted_by_user.display_name || r.deleted_by_user.username}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRevoke(r.id)}
                            disabled={isFetching || r.status === "revoked"}
                            className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow"
                          >
                            Revoke
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            disabled={isFetching}
                            className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-gray-900 mb-1">No Delegations Found</div>
                          <div className="text-sm text-gray-500">Try adjusting your filters or create a new delegation</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 p-5 bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
            <button
              disabled={filters.page <= 1}
              onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))}
              className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-ctb-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Page</span>
              <div className="px-4 py-2 bg-ctb-blue-200 text-white rounded-lg font-bold text-sm shadow-md">{filters.page}</div>
              <span className="text-sm font-medium text-gray-600">of</span>
              <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-bold text-sm border border-gray-200">{totalPages}</div>
            </div>
            <button
              disabled={filters.page >= totalPages}
              onClick={() => setFilters(f => ({ ...f, page: Math.min(totalPages, f.page + 1) }))}
              className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-ctb-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Manual Create Section */}
        {showAssignments && (
        <div ref={assignmentsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Single Delegation */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
            <div
              className="px-6 py-4 border-b border-gray-200"
              style={{ backgroundColor: "rgba(88, 204, 2, 0.10)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md" style={{ backgroundColor: "#58CC02" }}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Create Single Delegation</h2>
                  <p className="text-xs text-gray-600">Assign one delegation at a time</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Module
                </label>
                <div className="relative">
                  <select
                    className="w-full px-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent bg-white shadow-sm transition-all duration-200 appearance-none"
                    value={manualForm.scope}
                    onChange={e => setManualForm(f => ({ ...f, scope: e.target.value as Scope }))}
                  >
                    {scopes.map(s => (
                      <option key={s} value={s}>
                        {s === "retest" ? "Retest" : "Report Comment"}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  {manualForm.scope === "retest" ? "Retest ID" : "Report ID"}
                </label>
                <input
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent shadow-sm transition-all duration-200"
                  placeholder="Enter ID number..."
                  type="number"
                  value={manualForm.object_id || ""}
                  onChange={e => setManualForm(f => ({ ...f, object_id: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Assign To Researcher
                </label>
                <UserSearchDropdown
                  value={manualForm.to_user_id}
                  onChange={(userId, user) => setManualForm(f => ({ ...f, to_user_id: userId, to_user: user }))}
                  placeholder="Search by username..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Reason (Optional)
                </label>
                <input
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-blue-200 focus:border-transparent shadow-sm transition-all duration-200"
                  placeholder="e.g., Researcher on leave"
                  value={manualForm.reason || ""}
                  onChange={e => setManualForm(f => ({ ...f, reason: e.target.value }))}
                />
              </div>
              <button
                onClick={handleManualCreate}
                className="w-full px-4 py-3 bg-[#010D2C] text-white text-sm font-semibold rounded-lg hover:bg-[#02153f] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                disabled={!manualForm.to_user_id || !manualForm.object_id}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Delegation
              </button>
            </div>
          </div>

          {/* Bulk Delegation */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
            <div
              className="px-6 py-4 border-b border-gray-200"
              style={{ backgroundColor: "rgba(88, 204, 2, 0.10)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md" style={{ backgroundColor: "#58CC02" }}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Bulk Delegations</h2>
                  <p className="text-xs text-gray-600">Import multiple delegations via JSON</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">JSON Format:</label>
                <div className="bg-ctb-blue-150 border-2 border-ctb-blue-250 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <svg className="w-5 h-5 text-ctb-blue-200 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <code className="text-xs text-ctb-blue-900 block font-mono bg-white p-3 rounded-lg border border-ctb-blue-250 overflow-x-auto shadow-sm">
                        [{`{ "module": "retest"|"report_comment", "retest_id"|"report_id": 123, "to_username": "john_doe", "reason": "optional" }`}]
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Example:</label>
                <div className="bg-white border border-ctb-blue-250 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <svg className="w-5 h-5 text-ctb-blue-200 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    <div className="flex-1">
                      <code className="text-xs text-ctb-blue-900 block font-mono bg-white p-3 rounded-lg border border-ctb-blue-250 overflow-x-auto shadow-sm">
                        [{`{ "module": "retest", "retest_id": 5, "to_username": "researcher1" }, { "module": "report_comment", "report_id": 10, "to_username": "researcher2" }`}]
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Assignments (JSON)
                </label>
                <textarea
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ctb-green-300 focus:border-transparent font-mono bg-white shadow-sm transition-all duration-200"
                  placeholder='[{"module": "retest", "retest_id": 1, "to_username": "researcher1"}]'
                  value={bulkAssignmentsText}
                  onChange={e => setBulkAssignmentsText(e.target.value)}
                  rows={6}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleBulkManual}
                  className="flex-1 px-4 py-3 bg-ctb-green-300 text-white text-sm font-semibold rounded-lg hover:bg-ctb-green-500 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Submit Bulk Assignments
                </button>
                <button
                  onClick={() => setBulkAssignmentsText("")}
                  className="px-5 py-3 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default DelegationManagement;

